import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, MessageSquare, User, CreditCard, CheckCircle, AlertCircle, Calendar, MapPin, Camera, Clock, Edit, Plus, Zap, FileWarning } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminEventEditModal from '@/components/AdminEventEditModal';
import WhatsNewPanel from '@/components/WhatsNewPanel';
import { formatEventCost } from '@/lib/formatEventCost';
import { formatEventDate, relativeEventLabel, toLocalDateISO } from '@/lib/formatEventDate';
import { tierLabel } from '@/lib/tiers';

const THREE_MONTHS_MS = 1000 * 60 * 60 * 24 * 90;

function FeedbackWidget() {
  const [rating, setRating] = useState(0);
  const [sent, setSent] = useState(false);
  if (sent) return <p className="text-xs text-[#56dfb4] mt-2 font-medium">Thanks for your feedback!</p>;
  return (
    <div className="flex items-center gap-1 mt-2">
      {[1,2,3,4,5].map(n => (
        <button key={n} onClick={async () => {
          setRating(n);
          try { await api.post('/feedback', { type: 'meetup', rating: n, comment: '' }); setSent(true); toast.success('Feedback submitted!'); } catch { toast.error('Failed'); }
        }} className={`w-7 h-7 rounded-full text-xs font-bold border transition-all ${rating >= n ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-[#56c6df]'}`} data-testid={`feedback-star-${n}`}>{n}</button>
      ))}
      <span className="text-xs text-slate-400 ml-1">Rate 1-5</span>
    </div>
  );
}

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [myHostedEvents, setMyHostedEvents] = useState([]);
  const [draftEvents, setDraftEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [confirmingBio, setConfirmingBio] = useState(false);
  const [referralStatus, setReferralStatus] = useState(null);
  const [myGameRequests, setMyGameRequests] = useState([]);
  const [nearbyGameRequests, setNearbyGameRequests] = useState([]);

  useEffect(() => {
    if (user?.subscription_tier !== 'free') {
      api.get('/messages/unread-count').then(({ data }) => setUnreadCount(data.unread_count || 0)).catch(() => {});
    }
    // Fetch upcoming events (public feed within 50 mi)
    api.get('/events?max_distance=50').then(({ data }) => setUpcomingEvents((data.events || []).slice(0, 3))).catch(() => {});
    // Fetch the member's own hosted events. Live events power the "Your
    // upcoming events" rail; drafts (events stuck mid-payment) get
    // surfaced separately in a dedicated banner so the host can finish
    // paying — otherwise they think their post vanished.
    api.get('/events/my-events').then(({ data }) => {
      const all = data.events || [];
      setMyHostedEvents(all.filter(e => e.status !== 'canceled' && e.payment_status === 'published'));
      setDraftEvents(all.filter(e => e.status !== 'canceled' && e.payment_status === 'draft'));
    }).catch(() => {});
    // Pull referral counters so the instructor milestone widget can render.
    if (user && !user.is_demo) {
      api.get('/referrals/my-code').then(({ data }) => setReferralStatus(data)).catch(() => {});
    }
    // Active "Last-minute game requests" the member has hosted — surfaces a
    // one-click "Manage roster" path from the dashboard.
    if (user && !user.is_demo) {
      api.get('/game-requests/mine').then(({ data }) => setMyGameRequests(data.requests || [])).catch(() => {});
      // Open game requests *near* the member (paid tiers only — backend gates).
      api.get('/game-requests/nearby').then(({ data }) => setNearbyGameRequests(data.requests || [])).catch(() => {});
    }
  }, [user]);

  const isFree = user?.subscription_tier === 'free';
  const isProfileComplete = user?.profile_complete;
  const isDemo = !!user?.is_demo;
  const missingPhoto = !isDemo && !user?.profile_photo;
  const missingBio = !isDemo && (!user?.bio || !user.bio.trim());

  // Bio refresh: show reminder if bio exists but bio_last_updated is missing or > 3 months old
  let needsBioRefresh = false;
  if (!missingBio && !isDemo) {
    const lastUpdated = user?.bio_last_updated ? new Date(user.bio_last_updated).getTime() : 0;
    needsBioRefresh = !lastUpdated || (Date.now() - lastUpdated) > THREE_MONTHS_MS;
  }

  const confirmBioStillAccurate = async () => {
    setConfirmingBio(true);
    try {
      await api.post('/profile/confirm-bio');
      await refreshUser();
      toast.success('Thanks for confirming! Bio marked as current.');
    } catch {
      toast.error('Could not update — please try again.');
    } finally {
      setConfirmingBio(false);
    }
  };

  return (
    <div data-testid="dashboard-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Welcome back, {user?.display_name || user?.first_name}!
        </h1>
        <p className="text-slate-500 mt-1">Here's your dashboard overview</p>
      </div>

      <WhatsNewPanel />

      {/* Demo Account Banner */}
      {user?.is_demo && (
        <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#b456df]/10 to-[#56c6df]/10 border border-[#b456df]/30" data-testid="demo-account-banner">
          <div className="w-10 h-10 rounded-xl bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">&#128269;</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Demo Account — Instructor Unlimited Preview</p>
            <p className="text-xs text-slate-500 mt-0.5">
              You're exploring what an <strong>Instructor Unlimited membership</strong> looks like. Profile edits and messages to real players are disabled, and any events you post are kept private (not visible on the public Events board). Ready to join for real? <Link to="/pricing" className="text-[#b456df] font-semibold hover:underline">View membership plans</Link>.
            </p>
          </div>
        </div>
      )}

      {/* Draft events — surface unfinished posts so they don't disappear
          into a black hole. The host clicked "Pay & Publish", we created
          the draft, but PayPal capture never completed. /events/my-events
          has the full recovery UI (Complete Payment / Discard) — this
          banner just funnels the host there before they think their
          event vanished. */}
      {draftEvents.length > 0 && (
        <div
          className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200"
          data-testid="dashboard-draft-events-banner"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <FileWarning className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              You have {draftEvents.length} unpublished draft event{draftEvents.length === 1 ? '' : 's'}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              The posting fee wasn&apos;t completed, so {draftEvents.length === 1 ? "it's" : "they're"} not visible to other players yet.
              {draftEvents[0]?.title ? <> Most recent: <strong>{draftEvents[0].title}</strong>.</> : null}
            </p>
          </div>
          <Link
            to="/events/my-events"
            className="flex-shrink-0 px-4 py-2 rounded-full bg-[#56dfb4] text-slate-900 text-sm font-medium hover:bg-[#3ec49b] transition-colors"
            data-testid="dashboard-draft-events-cta"
          >
            Finish &amp; publish →
          </Link>
        </div>
      )}


      {/* Nearby Game Requests Banner — paid player tiers only (backend gates).
          Surfaces open last-minute games within 25 mi so users who didn't open
          their email still see the call to play. */}
      {nearbyGameRequests.length > 0 && (
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#df5681]/10 to-[#df5681]/5 border border-[#df5681]/30 p-4" data-testid="nearby-game-requests-banner">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#df5681]/15 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-[#df5681]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {nearbyGameRequests.length === 1 ? "A player nearby needs you" : `${nearbyGameRequests.length} nearby players need you`}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Last-minute game requests within 25 mi — tap to RSVP first-come, first-served.</p>
            </div>
          </div>
          <div className="space-y-2">
            {nearbyGameRequests.map(gr => {
              const confirmed = (gr.responders || []).filter(r => r.state === 'confirmed').length;
              const open = gr.seats_needed - confirmed;
              return (
                <Link
                  key={gr.request_id}
                  to={`/game-requests/${gr.request_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/80 hover:bg-white border border-[#df5681]/10 transition-colors"
                  data-testid={`nearby-game-request-${gr.request_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      <strong>{gr.host_name}</strong> needs {open} player{open === 1 ? '' : 's'}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {gr.game_when ? new Date(gr.game_when).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'TBD'}
                      <span>·</span>
                      <MapPin className="w-3 h-3" />
                      ~{gr.distance_miles} mi
                    </p>
                  </div>
                  <Button size="sm" className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] text-xs whitespace-nowrap" data-testid={`nearby-game-request-cta-${gr.request_id}`}>
                    I'm in
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Referral Promo Alert — always visible for logged-in members (except demo).
          Copy is the canonical product wording (instructor vs player paths). */}
      {user?.referral_code && !user?.is_demo && (
        <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-[#56dfb4]/10 border border-[#56dfb4]/20" data-testid="referral-promo-alert">
          <div className="w-10 h-10 rounded-xl bg-[#56dfb4]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">&#127873;</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {user?.is_instructor ? 'Instructor Referrals' : 'Player Referrals'}
            </p>
            {user?.is_instructor ? (
              <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                Share your referral code{' '}
                <strong className="text-[#56dfb4]">{user.referral_code}</strong>{' '}
                with players. When a player uses your code to upgrade to{' '}
                <strong>Verified Player</strong>, they get one free month and you earn
                credit toward your instructor membership. Refer 10 Verified Players
                and get one free month of <strong>Instructor Unlimited</strong>.
              </p>
            ) : (
              <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                Share your referral code{' '}
                <strong className="text-[#56dfb4]">{user.referral_code}</strong>{' '}
                with other players. When someone uses your code to upgrade to a{' '}
                <strong>Verified Player Membership</strong>, they get one free month, and
                you get one free month added to your membership too. There's no limit to
                how many free months you can earn.
              </p>
            )}
          </div>
          <Link to="/referrals"><Button size="sm" className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] text-xs whitespace-nowrap">Share Code</Button></Link>
        </div>
      )}

      {/* Instructor Milestone Tracker — only visible to instructors with at
          least 1 fulfilled referral OR an active reward. Shows progress to
          the next free month of Instructor Unlimited. */}
      {user?.is_instructor && !user?.is_demo && referralStatus && (referralStatus.instructor_referral_count > 0 || referralStatus.unlimited_reward_active) && (
        <div
          className="mb-6 rounded-2xl border-2 border-[#b456df]/30 bg-gradient-to-br from-[#b456df]/10 via-white to-[#56dfb4]/5 p-5"
          data-testid="instructor-milestone-tracker"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
              <span className="text-xl" aria-hidden="true">🏆</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Instructor Referral Progress
                </p>
                {referralStatus.unlimited_reward_active && (
                  <span className="px-2 py-0.5 rounded-full bg-[#56dfb4] text-slate-900 text-[10px] font-bold uppercase tracking-wider" data-testid="unlimited-reward-active-badge">
                    Bonus Month Active
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 mb-3">
                <strong className="text-[#b456df]" data-testid="instructor-referral-count">{referralStatus.instructor_referral_count}</strong>{' '}
                Verified Player {referralStatus.instructor_referral_count === 1 ? 'referral' : 'referrals'} so far. Reach{' '}
                <strong>{referralStatus.next_milestone_at}</strong> to earn another free month of Instructor Unlimited.
              </p>
              <div className="w-full bg-white rounded-full h-2.5 overflow-hidden border border-slate-200" data-testid="milestone-progress-bar">
                <div
                  className="h-full bg-gradient-to-r from-[#b456df] to-[#56dfb4] transition-all duration-700"
                  style={{ width: `${Math.min(100, (referralStatus.progress_to_milestone / 10) * 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[11px] text-slate-500">{referralStatus.progress_to_milestone} / 10</span>
                <Link to="/referrals" className="text-[11px] text-[#b456df] hover:underline font-semibold" data-testid="milestone-share-link">
                  Share my code &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Cards */}
      {(!isProfileComplete || isFree || missingPhoto || missingBio || needsBioRefresh) && (
        <div className="mb-8 space-y-3">
          {!isProfileComplete && (
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-100" data-testid="dash-alert-profile-incomplete">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Complete your profile</p>
                <p className="text-xs text-amber-600">Add your zip code and preferences to get connected with players.</p>
              </div>
              <Link to="/profile"><Button size="sm" className="rounded-full bg-amber-500 text-white hover:bg-amber-600">Complete Profile</Button></Link>
            </div>
          )}

          {missingPhoto && (
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#56c6df]/5 border border-[#56c6df]/20" data-testid="dash-alert-missing-photo">
              <div className="w-9 h-9 rounded-xl bg-[#56c6df]/15 flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-[#56c6df]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">Add a profile picture</p>
                <p className="text-xs text-slate-500">Profiles with photos get more connection requests from other players.</p>
              </div>
              <Link to="/profile"><Button size="sm" className="rounded-full bg-[#56c6df] text-white hover:bg-[#3aa8c0]" data-testid="dash-add-photo-btn">Upload Photo</Button></Link>
            </div>
          )}

          {missingBio && (
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#b456df]/5 border border-[#b456df]/20" data-testid="dash-alert-missing-bio">
              <div className="w-9 h-9 rounded-xl bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-[#b456df]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">Tell players about yourself</p>
                <p className="text-xs text-slate-500">Add a short bio so others can get to know you before scheduling a game.</p>
              </div>
              <Link to="/profile"><Button size="sm" className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4]" data-testid="dash-add-bio-btn">Add Bio</Button></Link>
            </div>
          )}

          {needsBioRefresh && (
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#56dfb4]/5 border border-[#56dfb4]/20" data-testid="dash-alert-bio-refresh">
              <div className="w-9 h-9 rounded-xl bg-[#56dfb4]/15 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-[#56dfb4]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">Is your bio still accurate?</p>
                <p className="text-xs text-slate-500">It's been a while since you updated your bio. Keep it fresh so players see the real you.</p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={confirmBioStillAccurate}
                  disabled={confirmingBio}
                  className="rounded-full border-[#56dfb4] text-[#3ec49b] hover:bg-[#56dfb4]/10"
                  data-testid="dash-bio-still-accurate-btn"
                >
                  {confirmingBio ? 'Saving…' : "It's still accurate"}
                </Button>
                <Link to="/profile">
                  <Button size="sm" className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]" data-testid="dash-update-bio-btn">Update Bio</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link to="/find-players" className="block">
          <Card className="hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer border-slate-100" data-testid="dash-find-players-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#56dfb4]/10 flex items-center justify-center">
                <Search className="w-6 h-6 text-[#56dfb4]" />
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Find Players</p>
                <p className="text-xs text-slate-500">{isFree ? 'Upgrade required' : 'Search nearby'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/messages" className="block">
          <Card className="hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer border-slate-100" data-testid="dash-messages-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#56c6df]/10 flex items-center justify-center relative">
                <MessageSquare className="w-6 h-6 text-[#56c6df]" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#df5681] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{unreadCount}</span>
                )}
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</p>
                <p className="text-xs text-slate-500">{unreadCount > 0 ? `${unreadCount} unread` : 'No new messages'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/profile" className="block">
          <Card className="hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer border-slate-100" data-testid="dash-profile-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#df5681]/10 flex items-center justify-center">
                <User className="w-6 h-6 text-[#df5681]" />
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>My Profile</p>
                <p className="text-xs text-slate-500">{isProfileComplete ? 'Complete' : 'Needs attention'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/subscription" className="block">
          <Card className="hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer border-slate-100" data-testid="dash-subscription-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#b456df]/10 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-[#b456df]" />
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscription</p>
                <p className="text-xs text-slate-500">{tierLabel(user?.subscription_tier)} plan</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Profile Summary */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Profile Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-slate-50">
              <p className="text-xs text-slate-500 mb-1">Skill Level</p>
              <p className="font-medium text-sm capitalize">{user?.skill_level || 'Not set'}</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-50">
              <p className="text-xs text-slate-500 mb-1">Location</p>
              <p className="font-medium text-sm">{user?.zip_code || 'Not set'}</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-50">
              <p className="text-xs text-slate-500 mb-1">Availability</p>
              <p className="font-medium text-sm">{user?.days_available?.length || 0} days/week</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-50">
              <p className="text-xs text-slate-500 mb-1">Mahjong Set</p>
              <p className="font-medium text-sm capitalize">{user?.has_mahjong_set || 'Not set'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Hosted Events — quick access for hosts to view + edit their events */}
      {myHostedEvents.length > 0 && (
        <Card className="border-slate-100 mt-6" data-testid="dash-my-hosted-events">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>My Hosted Events</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">{myHostedEvents.length} live event{myHostedEvents.length !== 1 ? 's' : ''} you've posted</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/events/my-events" className="text-sm text-[#56c6df] hover:underline" data-testid="dash-my-events-view-all">View All</Link>
              <Link to="/events/post">
                <Button size="sm" className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-1 text-xs" data-testid="dash-post-event-btn">
                  <Plus className="w-3 h-3" /> Post
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {myHostedEvents.slice(0, 5).map(event => (
                <div key={event.event_id} className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`dash-my-event-${event.event_id}`}>
                  <Link to={`/events/${event.event_id}`} className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-[#df5681]/10 flex flex-col items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 text-[#df5681]" />
                      <span className="text-[10px] font-bold mt-0.5">{event.event_date ? formatEventDate(event.event_date, { month: 'short', day: 'numeric' }) : ''}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{event.title}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {event.venue_name}{event.city && `, ${event.city}`}
                      </p>
                    </div>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingEvent(event)}
                    className="rounded-full gap-1 border-[#b456df] text-[#b456df] hover:bg-[#b456df]/10 text-xs"
                    data-testid={`dash-edit-event-${event.event_id}`}
                  >
                    <Edit className="w-3 h-3" /> Edit
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Active Game Requests — last-minute requests still open/full */}
      {myGameRequests.length > 0 && (
        <Card className="border-slate-100 mt-6" data-testid="dash-my-game-requests">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#df5681]" />
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>My Active Game Requests</CardTitle>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Manage roster, see who's RSVP'd, or cancel.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {myGameRequests.map(gr => {
                const confirmed = (gr.responders || []).filter(r => r.state === 'confirmed').length;
                const waitlist = (gr.responders || []).filter(r => r.state === 'waitlist').length;
                return (
                  <Link
                    key={gr.request_id}
                    to={`/game-requests/${gr.request_id}`}
                    className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`dash-game-request-${gr.request_id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#df5681]/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-[#df5681]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{confirmed}/{gr.seats_needed} confirmed{waitlist > 0 && ` · ${waitlist} waitlist`}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {gr.game_when ? formatEventDate(toLocalDateISO(gr.game_when), { month: 'short', day: 'numeric' }) : 'TBD'}
                        {gr.where_text && <span className="truncate"> · {gr.where_text}</span>}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${gr.status === 'full' ? 'bg-amber-100 text-amber-800' : 'bg-[#56dfb4]/20 text-slate-900'}`}>
                      {gr.status === 'full' ? 'Full' : 'Open'}
                    </span>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <Card className="border-slate-100 mt-6" data-testid="dash-upcoming-events">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Upcoming Events Near You</CardTitle>
            <Link to="/events" className="text-sm text-[#56c6df] hover:underline">View All</Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingEvents.map(event => (
                <Link key={event.event_id} to={`/events/${event.event_id}`} className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-[#56dfb4]/10 flex flex-col items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-[#56dfb4]" />
                    <span className="text-[10px] font-bold mt-0.5">{event.event_date ? formatEventDate(event.event_date, { month: 'short', day: 'numeric' }) : ''}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate flex items-center gap-2">
                      {event.title}
                      {(() => {
                        const rel = relativeEventLabel(event.event_date);
                        if (!rel) return null;
                        const tone =
                          rel === 'Today' ? 'bg-[#df5681] text-white' :
                          rel === 'Tomorrow' ? 'bg-[#56dfb4] text-slate-900' :
                          'bg-slate-100 text-slate-700';
                        return (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tone}`}>{rel}</span>
                        );
                      })()}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {event.venue_name}{event.city && `, ${event.city}`}
                      {event.distance !== null && event.distance !== undefined && ` (~${event.distance} mi)`}
                    </p>
                  </div>
                  {event.is_free ? (
                    <span className="text-xs font-medium text-[#56dfb4] bg-[#56dfb4]/10 px-2 py-1 rounded-full">FREE</span>
                  ) : (
                    <span className="text-xs text-slate-500">{formatEventCost(event)}</span>
                  )}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feedback & Referral */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Card className="border-slate-100">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#56c6df]/10 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-[#56c6df]" />
            </div>
            <div>
              <h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>How was your meetup?</h3>
              <p className="text-xs text-slate-500 mt-0.5">Share your experience to help us improve the community.</p>
              <FeedbackWidget />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#56dfb4]/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-[#56dfb4]" />
            </div>
            <div>
              <h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Refer a Friend</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {user?.is_instructor
                  ? '10 Verified Player referrals = 1 free month of Instructor Unlimited.'
                  : "You both get 1 free month when they upgrade."}
              </p>
              <Link to="/referrals"><Button size="sm" className="mt-2 rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] text-xs" data-testid="dashboard-referral-btn">Get Referral Code</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {editingEvent && (
        <AdminEventEditModal
          event={editingEvent}
          mode="host"
          onClose={() => setEditingEvent(null)}
          onSaved={updated => {
            setMyHostedEvents(prev => prev.map(e => e.event_id === updated.event_id ? { ...e, ...updated } : e));
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
}
